import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, limit as firestoreLimit, updateDoc, doc } from 'firebase/firestore';
import { ChefHat, ArrowLeft, Search, Clock, CheckCircle2, Lock, Calendar } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import { getCategoryEmoji } from '../constants/emojis';
import { useSubscriptionLimits } from '../hooks/useSubscription';

interface ExpiringItem {
  id: string;
  name: string;
  category: string;
  expirationDate: string;
  daysUntilExpiration: number;
  photoURL?: string;
  allergens?: string[];
}

interface MealSuggestion {
  type: string;
  icon: string;
  ingredients: string[];
  description: string;
}

export function CookBeforeExpires() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const subscription = useSubscriptionLimits();
  const [expiringItems, setExpiringItems] = useState<ExpiringItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [mealSuggestions, setMealSuggestions] = useState<MealSuggestion[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadExpiringItems();
  }, [user]);

  useEffect(() => {
    // Generate meal suggestions based on selected items
    if (selectedItems.size > 0) {
      generateMealSuggestions();
    } else {
      setMealSuggestions([]);
    }
  }, [selectedItems, expiringItems]);

  async function loadExpiringItems() {
    try {
      // Get user's household
      const householdsQuery = query(
        collection(db, 'households'),
        where('members', 'array-contains', user!.uid),
        firestoreLimit(1)
      );

      const householdsSnapshot = await getDocs(householdsQuery);
      if (householdsSnapshot.empty) {
        setLoading(false);
        return;
      }

      const householdId = householdsSnapshot.docs[0].id;

      // Get items expiring in next 5 days
      const itemsQuery = query(
        collection(db, 'items'),
        where('householdId', '==', householdId),
        where('status', '==', 'active')
      );

      const itemsSnapshot = await getDocs(itemsQuery);
      const now = new Date();

      const expiring: ExpiringItem[] = itemsSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          const expirationDate = new Date(data.expirationDate);
          const daysUntil = differenceInDays(expirationDate, now);

          return {
            id: doc.id,
            name: data.name,
            category: data.category,
            expirationDate: data.expirationDate,
            daysUntilExpiration: daysUntil,
            photoURL: data.photoURL,
            allergens: data.allergens,
          };
        })
        .filter((item) => item.daysUntilExpiration <= 5 && item.daysUntilExpiration >= 0)
        .sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration);

      setExpiringItems(expiring);
      setLoading(false);
    } catch (err) {
      console.error('Error loading items:', err);
      setLoading(false);
    }
  }

  function generateMealSuggestions() {
    const selected = expiringItems.filter((item) => selectedItems.has(item.id));
    const categories = selected.map((item) => item.category.toLowerCase());
    const names = selected.map((item) => item.name.toLowerCase());

    const suggestions: MealSuggestion[] = [];

    // Stir Fry - needs protein + veggies
    const hasProtein = names.some((n) =>
      ['chicken', 'beef', 'pork', 'tofu', 'shrimp', 'fish'].some((p) => n.includes(p))
    );
    const veggieCount = selected.filter((item) =>
      ['carrot', 'broccoli', 'pepper', 'onion', 'mushroom', 'cabbage', 'zucchini'].some((v) =>
        item.name.toLowerCase().includes(v)
      )
    ).length;

    if (hasProtein && veggieCount >= 2) {
      suggestions.push({
        type: 'Stir Fry',
        icon: '🍜',
        ingredients: selected.map((i) => i.name),
        description: 'Quick stir fry with your protein and vegetables',
      });
    }

    // Soup - needs 3+ veggies or broth
    const hasBroth = names.some((n) => n.includes('broth') || n.includes('stock'));
    if (veggieCount >= 3 || (veggieCount >= 2 && hasBroth)) {
      suggestions.push({
        type: 'Soup',
        icon: '🍲',
        ingredients: selected.map((i) => i.name),
        description: 'Hearty soup with fresh vegetables',
      });
    }

    // Pasta - needs pasta + sauce or protein
    const hasPasta = names.some((n) => n.includes('pasta') || n.includes('noodle') || n.includes('spaghetti'));
    const hasSauce = names.some((n) =>
      ['sauce', 'tomato', 'marinara', 'alfredo'].some((s) => n.includes(s))
    );

    if (hasPasta && (hasSauce || hasProtein)) {
      suggestions.push({
        type: 'Pasta Dish',
        icon: '🍝',
        ingredients: selected.map((i) => i.name),
        description: 'Classic pasta with your ingredients',
      });
    }

    // Salad - needs greens + toppings
    const hasGreens = names.some((n) =>
      ['lettuce', 'spinach', 'arugula', 'kale', 'salad'].some((g) => n.includes(g))
    );
    const hasToppings = selected.filter((item) =>
      ['tomato', 'cucumber', 'cheese', 'avocado', 'onion', 'pepper'].some((t) =>
        item.name.toLowerCase().includes(t)
      )
    ).length;

    if (hasGreens && hasToppings >= 2) {
      suggestions.push({
        type: 'Fresh Salad',
        icon: '🥗',
        ingredients: selected.map((i) => i.name),
        description: 'Crisp salad with fresh toppings',
      });
    }

    // Omelette/Scramble - needs eggs
    const hasEggs = names.some((n) => n.includes('egg'));
    if (hasEggs && selected.length >= 2) {
      suggestions.push({
        type: 'Omelette',
        icon: '🍳',
        ingredients: selected.map((i) => i.name),
        description: 'Fluffy omelette with your ingredients',
      });
    }

    setMealSuggestions(suggestions);
  }

  function toggleItem(itemId: string) {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  }

  async function handleMarkAsUsed() {
    if (selectedItems.size === 0) return;

    try {
      const updatePromises = Array.from(selectedItems).map((itemId) =>
        updateDoc(doc(db, 'items', itemId), {
          status: 'used',
          usedAt: new Date().toISOString(),
        })
      );

      await Promise.all(updatePromises);

      // Reload items
      setSelectedItems(new Set());
      await loadExpiringItems();
    } catch (err) {
      console.error('Error marking items as used:', err);
    }
  }

  function searchRecipes() {
    const ingredientNames = expiringItems
      .filter((item) => selectedItems.has(item.id))
      .map((item) => item.name)
      .join(' ');

    const searchQuery = encodeURIComponent(`recipes with ${ingredientNames}`);
    window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-bg flex items-center justify-center">
        <div className="text-center">
          <ChefHat className="w-16 h-16 text-primary-green mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading expiring items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-bg pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-primary-green transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Cook Before It Expires</h1>
              <p className="text-sm text-gray-600">Plan meals with expiring ingredients</p>
            </div>
            {!subscription.hasMealPlanning && (
              <Link
                to="/pricing"
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-yellow-600 transition-all flex items-center gap-2 text-sm"
              >
                <Lock className="w-4 h-4" />
                Upgrade
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Premium Feature Banner */}
        {!subscription.hasMealPlanning && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-lg p-6 mb-6 text-white">
            <div className="flex items-start gap-4">
              <Calendar className="w-12 h-12 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Unlock Full Meal Planning</h3>
                <p className="text-sm opacity-90 mb-4">
                  Premium users get meal planning calendar, bulk actions, and advanced meal suggestions!
                </p>
                <Link
                  to="/pricing"
                  className="inline-block bg-white text-orange-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm"
                >
                  Upgrade to Premium
                </Link>
              </div>
            </div>
          </div>
        )}

        {expiringItems.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Items Expiring Soon!</h2>
            <p className="text-gray-600 mb-6">
              Great job! You don't have any items expiring in the next 5 days.
            </p>
            <Link
              to="/dashboard"
              className="inline-block bg-primary-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all"
            >
              Back to Dashboard
            </Link>
          </div>
        ) : (
          <>
            {/* Expiring Items List */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-warning" />
                Expiring in 5 Days ({expiringItems.length})
              </h2>

              <div className="space-y-3">
                {expiringItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedItems.has(item.id)
                        ? 'border-primary-green bg-primary-mint/20'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                        selectedItems.has(item.id)
                          ? 'bg-primary-green border-primary-green'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedItems.has(item.id) && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>

                    {item.photoURL ? (
                      <img src={item.photoURL} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                        {getCategoryEmoji(item.category)}
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <p className="text-sm text-gray-600">
                        {item.daysUntilExpiration === 0
                          ? 'Expires today'
                          : `Expires in ${item.daysUntilExpiration} day${item.daysUntilExpiration > 1 ? 's' : ''}`}
                      </p>
                    </div>

                    <div
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.daysUntilExpiration <= 1
                          ? 'bg-red-100 text-red-800'
                          : item.daysUntilExpiration <= 3
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {item.daysUntilExpiration}d
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Meal Suggestions */}
            {mealSuggestions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ChefHat className="w-5 h-5 sm:w-6 sm:h-6 text-primary-green" />
                  Meal Suggestions
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {mealSuggestions.map((meal, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-xl p-4 hover:border-primary-green transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">{meal.icon}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{meal.type}</h3>
                          <p className="text-xs text-gray-600">{meal.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {meal.ingredients.map((ing, i) => (
                          <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {ing}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {selectedItems.size > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">
                  Actions ({selectedItems.size} selected)
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <button
                    onClick={searchRecipes}
                    className="bg-blue-600 text-white py-3 sm:py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 touch-manipulation"
                  >
                    <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-base">Search Recipes</span>
                  </button>

                  <button
                    onClick={handleMarkAsUsed}
                    className="bg-primary-green text-white py-3 sm:py-3.5 rounded-xl font-semibold hover:bg-primary-green/90 transition-all flex items-center justify-center gap-2 touch-manipulation"
                  >
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-base">Mark as Used</span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
